import type Article from "models/Article";
import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useParams } from "react-router";
import articlesService from "services/articlesService";
import NotFoundErrorPage from "../errors/NotFoundErrorPage";
import ArticleInformationTemplate from "components/templates/ArticleInformationTemplate";
import ModalPageTemplate from "components/templates/ModalPageTemplate";
import ArticleInformationSkeleton from "components/templates/ArticleInformationSkeleton";
import type PatchData from "models/PatchData";
import { useCatalogue } from "providers";

function ArticleInformationPage(): ReactNode {
  const { articleId } = useParams();
  const [article, setArticle] = useState<Article>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { updateCatalog } = useCatalogue();

  useEffect(() => {
    async function fetchData(): Promise<void> {
      if (articleId != null) {
        setIsLoading(true);
        setArticle(await articlesService.getById(articleId));
      }
      setIsLoading(false);
    }

    void fetchData();
  }, [articleId]);

  const onChangeArticleActive = useCallback(async (): Promise<void> => {
    if (article != null) {
      const patchData: Array<PatchData> = [];
      patchData.push({ op: "replace", path: "isActive", value: !article.isActive });
      const newArticle = await articlesService.patch(article.id, patchData);
      setArticle(newArticle);
      void updateCatalog();
    }
  }, [article, updateCatalog]);

  if (isLoading) {
    return (
      <ModalPageTemplate>
        <ArticleInformationSkeleton />
      </ModalPageTemplate>
    );
  }
  if (article == null || articleId == null) {
    return (
      <ModalPageTemplate>
        <NotFoundErrorPage />
      </ModalPageTemplate>
    );
  }
  return (
    <ModalPageTemplate>
      <ArticleInformationTemplate article={article} onToggleActive={onChangeArticleActive} />
    </ModalPageTemplate>
  );
}

export default ArticleInformationPage;
